import updateTaskStudents from '@/api/teacher/tasks/updateTaskStudents.endpoint';
import useCustomHook from '@/hooks/useCustomHook';
import {
  createActivityTypeConf,
  getActivityConfDefaults,
} from '@/modules/activities/ActivityTypesConf';
import openAPIErrorNotification from '@/shared/APIErrorNotification/APIErrorNotification';
import { RealTimeActivityContext } from '@/shared/RealTimeActivityContext/RealTimeActivityContext';
import { useContext, useEffect } from 'react';
import getTeachersClasses from '@/api/classes/getTeachersClasses.endpoint';
import { GlobalContext } from '@/shared/GlobalContext/GlobalContext';
import { useRouter } from 'next/router';
import { notification } from 'antd';
import getActivity from '@/api/content/getActivity.endpoint';

export default function useLaunchRealTimeActivity({ clazz, task, activityId }) {
  const realTimeActivityContext = useContext(RealTimeActivityContext);
  const globalContext = useContext(GlobalContext);
  const router = useRouter();

  const { state, actions } = useCustomHook({
    defaultState: {
      loading: true,
      processing: false,
      ending: false,
      selectedActivity: null,
      selectedStudents: [],
      changeStudents: false,
      conf: null,
      classes: [],
      selectedClass: null,
      activities: [],
    },
    actions: {
      initForm: async (isTeacherPro) => {
        await realTimeActivityContext.actions.checkExistingRealTimeActivity();
        if (clazz) {
          actions.setState({
            selectedClass: clazz,
          });
        } else {
          if (isTeacherPro) {
            const classes = await getTeachersClasses({
              teacherId: globalContext.state.user?.teacher?.id,
            });
            actions.setState({
              classes: [
                ...classes,
                {
                  id: '',
                  name: 'Shared student account',
                  internalUse: true,
                },
              ],
            });
          } else {
            actions.setState({
              selectedClass: { internalUse: true },
            });
          }
        }
        actions.setState({ loading: false });
      },
      setSelectedClass: (selectedClass) => {
        actions.setState({ selectedClass });
      },
      setSelectedActivity: async (selectedActivity) => {
        actions.setState({ loading: true });
        const newState = {
          selectedActivity: state.activities.find(
            (activity) => activity.id == selectedActivity.id
          ),
          loading: false,
        };
        if (!newState.selectedActivity) {
          newState.selectedActivity = await getActivity(selectedActivity.id);
          newState.activities = [
            ...state.activities,
            newState.selectedActivity,
          ];
        }
        actions.setState(newState);
      },
      setSelectedStudents: (selectedStudents) => {
        actions.setState({ selectedStudents });
      },
      setChangeStudents: (changeStudents) => {
        actions.setState({ changeStudents });
      },
      setConf: (conf) => {
        actions.setState({ conf });
      },
      launch: async () => {
        try {
          actions.setState({ processing: true });
          if (task) {
            if (state.changeStudents) {
              await updateTaskStudents({
                taskId: task.id,
                studentIds: state.selectedStudents.map(
                  (selectedStudent) => selectedStudent.id
                ),
              });
            }
            await realTimeActivityContext.actions.run({
              realTimeActivityId: task.realTimeActivity.id,
            });
          } else {
            const activity = state.selectedActivity;
            const data = {
              activityId: activity.id,
              activityConf: getTypeActivityConf(activity),
              classId: state.selectedClass.id,
              createTask: true,
              studentIds: state.changeStudents
                ? state.selectedStudents.map((student) => student.id)
                : null,
              realTimeData: { ...state.conf, returnUrl: router.asPath },
            };
            await realTimeActivityContext.actions.run({ data });
          }
        } catch (e) {
          actions.setState({ processing: false });
          openAPIErrorNotification({
            title: 'There was an error',
            description: 'There was a problem staring the live task',
            errorFeedback: e.feedbackData,
          });
        }
      },
      resumeActivity: () => {
        router.push('/teacher/liveTask');
      },
      endActivity: async () => {
        actions.setState({ ending: true });
        await realTimeActivityContext.actions.finish();
        notification.success({
          message: 'Task ended',
          description: 'You can launch a new live task now',
        });
      },
    },
  });

  useEffect(() => {
    if (globalContext.state.loaded) {
      actions.initForm(globalContext.state.isTeacherPro);
    }
  }, [globalContext.state.loaded, globalContext.state.user]);

  useEffect(() => {
    if (activityId) {
      actions.setSelectedActivity({ id: activityId });
    }
  }, [activityId]);

  return {
    state: {
      ...state,
      runningRealTimeActivity: realTimeActivityContext.state.realTimeActivity,
      isTeacherPro: globalContext.state.isTeacherPro,
    },
    actions,
  };
}

function getTypeActivityConf(activity) {
  const activityTypeConf = createActivityTypeConf(activity);
  return getActivityConfDefaults(activityTypeConf);
}
